import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Users, BookOpen, ShieldOff } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator } from '../../../mascot';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import type { Classroom } from '../../../accounts';
interface CoTeacherWelcomeModalProps {
  open: boolean;
  classroom: Classroom | null;
  onClose: () => void;
}
/**
 * One-time welcome shown after a teacher accepts a co-teacher invite.
 *
 * Djay-zone: YES (Zone 1 — first-run / celebration).
 * Wave 3.3 — Co-teacher accept flow.
 */
export function CoTeacherWelcomeModal({
  open,
  classroom,
  onClose
}: CoTeacherWelcomeModalProps) {
  useEscapeKey(onClose, open && classroom !== null);
  return <AnimatePresence data-id="element-9525" data-mp="1kfup1wjgjx1s">
      {open && classroom && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="co-teacher-welcome-title" data-id="element-9526" data-mp="1kejo04hed3ed">
        
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-9527" data-mp="2r0qxnctr2j99" />
        
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-9528" data-mp="2i2omi4gaya89">
          
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-indigo-400 to-amber-400" data-id="element-9529" data-mp="30rd8ktqiullm" />

            <div className="p-8 text-center" data-id="element-9530" data-mp="1pcg369a15js3">
              <div className="flex justify-center mb-4" data-id="element-9531" data-mp="1pboivulvv8h4">
                <div className="relative" data-id="element-9532" data-mp="1ub9im78vnkuw">
                  <DjayNarrator size="md" emotion="celebrating" data-id="element-9533" data-mp="1ucc768iq9b7n" />
                  <Sparkles className="absolute -top-1 -right-2 w-5 h-5 text-amber-400" data-id="element-9534" data-mp="1a9y7d3uf8jh1" />
                </div>
              </div>

              <h2 id="co-teacher-welcome-title" className="text-2xl font-black text-slate-900 mb-2" data-id="element-9535" data-mp="1694h43tiaskz">
              
                Welkom in {classroom.name}!
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-6" data-id="element-9536" data-mp="witd6h9toje3">
                Je bent toegevoegd als{' '}
                <span className="font-bold" data-id="element-9537" data-mp="rjq6kse72540">co-docent</span>. Hieronder zie je
                wat je kunt doen.
              </p>

              <ul className="text-left space-y-3 mb-6" data-id="element-9538" data-mp="mlyjcbohv765">
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-9539" data-mp="ija7dv4hhk4y">
                  <Users className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-9540" data-mp="dkh3muf017hz" />
                  <div data-id="element-9541" data-mp="2cwjlk07z5ttj">
                    <p className="text-sm font-bold text-slate-900" data-id="element-9542" data-mp="2cvro0juthm0m">
                      Leerlingen begeleiden
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-9543" data-mp="2fgpny3gxxc7z">
                      Je ziet alle leerlingen, hun voortgang en kunt leerlingen
                      toevoegen of verwijderen.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-9544" data-mp="2p7u1cyqywp0p">
                  <BookOpen className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-9545" data-mp="2u52nfaf8ld78" />
                  <div data-id="element-9546" data-mp="1wj58to3z9s0k">
                    <p className="text-sm font-bold text-slate-900" data-id="element-9547" data-mp="1wjojcjrate4k">
                      Huiswerk &amp; toetsen
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-9548" data-mp="26g9pj8f20v9z">
                      Je kunt zelfstandig huiswerk en toetsen klaarzetten voor
                      deze klas.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-9549" data-mp="3huet40bcv34s">
                  <ShieldOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9550" data-mp="3lv73iwvyre2n" />
                  <div data-id="element-9551" data-mp="3kge61ty21pwi">
                    <p className="text-sm font-bold text-amber-900" data-id="element-9552" data-mp="3khp7vo7xwnkz">
                      Klas-structuur blijft bij eigenaar
                    </p>
                    <p className="text-xs text-amber-800" data-id="element-9553" data-mp="3ue2cvi8xqnv1">
                      Alleen de klas-eigenaar kan de klas hernoemen, verwijderen
                      of andere co-docenten beheren.
                    </p>
                  </div>
                </li>
              </ul>

              <Button onClick={onClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl text-sm font-bold transition-all" data-id="element-9554" data-mp="2a9j9lwkht5e">
              
                Aan de slag
              </Button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}